/**
 * Normalizes a text string by converting it to lowercase, removing accents/diacritics,
 * and keeping only alphanumeric characters (a-z, 0-9).
 *
 * This function is useful for text comparison, search operations, and creating
 * normalized identifiers or slugs.
 *
 * @param rawText - The text string to normalize. Can be null or undefined.
 * @returns A normalized string containing only lowercase letters and numbers.
 *          Returns an empty string if the input is null, undefined, or empty.
 *
 * @example
 * ```typescript
 * normalizeString('Héllo Wörld!'); // Returns: 'helloworld'
 * normalizeString('São Paulo - 123'); // Returns: 'saopaulo123'
 * normalizeString(null); // Returns: ''
 * normalizeString(''); // Returns: ''
 * ```
 *
 * @remarks
 * The normalization process:
 * 1. Converts the text to lowercase
 * 2. Applies Unicode Normalization Form D (NFD) to decompose combined characters
 * 3. Removes all diacritical marks (accents) using Unicode range \u0300-\u036f
 * 4. Removes all non-alphanumeric characters (keeps only a-z and 0-9)
 */
export function normalizeString(rawText: string | null | undefined): string {
  if (!rawText) return '';
  return rawText
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}
